// Base Libs
import {
    AnswerOption,
    AnswerOptionBuiltInTypeEnum,
    EvaluationQuestion,
    EvaluationQuestionAutomatedScoringFocusEnum,
    EvaluationQuestionScore,
    EvaluationFormEvaluationFormTypeEnum,
} from "@genesys/cloud-frontend/models";
import { createSelector } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

// Base commons
import { RootState } from "app/store";
import { DefaultLimits } from "constants/DefaultLimits";

// Feature slices
import { TemplateQuestion } from "./qualityFormTypes";
import { QualityFormState } from "./qualityFormStates";

// Quality form commons
import { BaseQuestionType } from "../common/BaseQuestionType";

// Form Editor
import {
    EVALUATION_YES_NO_QUESTION,
    SubQuestionType,
    QuestionInfoType,
} from "../formEditor/common/QuestionType";
import NPSScoreCategories from "../formEditor/common/NPSScoreCategories";

// Locales
import { Locale, renderString } from "i18n";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import { selectEvaluationFormAiScoringQuestionsMaxOuterLimit } from "features/qualityForm/outerLimitSlice";
import FeatureToggle from "enums/FeatureToggle";
import FormType from "enums/FormType";

// Private functions
const selectMCQAnswerOption = (state: RootState, answerOptionId: string) => {
    const data = state.qualityForm.data;
    return data.answerOptions?.[answerOptionId];
};

// Public functions
export const isUndefinedOrNull = (value) => {
    return value === undefined || value === null;
};

export const getAnswerOptionsTemplate = (
    subQuestionType: SubQuestionType,
    locale: Locale
) => {
    if (subQuestionType === SubQuestionType.YES_OR_NO) {
        return [
            {
                text: renderString(
                    locale,
                    "formEditor.QuestionGroupList.addQuestion.answerOptions.yes"
                ),
                value: 1,
            },
            {
                text: renderString(
                    locale,
                    "formEditor.QuestionGroupList.addQuestion.answerOptions.no"
                ),
                value: 0,
            },
        ];
    } else if (subQuestionType === SubQuestionType.MULTIPLE_CHOICE) {
        return Array.from({ length: 3 }, () => {
            return { text: "", value: 0 };
        });
    } else if (subQuestionType === SubQuestionType.RANGE) {
        return Array.from({ length: 5 }, (_, i) => {
            return { text: (i + 1).toString(), value: i + 1 };
        });
    } else {
        return [];
    }
};

export const getBaseQuestionTemplate = (
    questionType: QuestionInfoType
): any => {
    return {
        naEnabled: false,
        text: "",
        isKill: false,
        commentsRequired: false,
        answerOptions: [],
        helpText: "",
        isCritical: false,
        type: questionType.questionType,
        maxResponseCharacters: DefaultLimits.initMaxResponseCharacters,
        explanationPrompt: "",
        contextId: undefined,
        multipleSelectOptionQuestions: [],
    };
};

export const getMultipleSelectOptionQuestion = (): EvaluationQuestion => {
    // Just pass yes/no so the type is multiple choice.
    const mSelectQ = getBaseQuestionTemplate(EVALUATION_YES_NO_QUESTION);
    mSelectQ.answerOptions = [
        {
            id: uuidv4(),
            builtInType: "Unselected",
            value: 0,
        },
        {
            id: uuidv4(),
            builtInType: "Selected",
            value: 0,
            assistanceConditions: [],
        },
    ];
    return mSelectQ;
};

export const getMultipleSelectOptionQuestions = (): EvaluationQuestion[] => {
    const result: EvaluationQuestion[] = Array.from(
        { length: DefaultLimits.minMultipleSelectQuestionOptionCount },
        () => getMultipleSelectOptionQuestion()
    );
    return result;
};

export const getQuestionTemplate = (
    questionType: QuestionInfoType,
    locale: Locale,
    isMultipleSelectFTEnabled = false
): TemplateQuestion => {
    const result: TemplateQuestion = getBaseQuestionTemplate(
        questionType
    ) as TemplateQuestion;
    result.answerOptions = getAnswerOptionsTemplate(
        questionType.subQuestionType,
        locale
    );
    if (
        isMultipleSelectFTEnabled &&
        questionType.subQuestionType === SubQuestionType.MULTIPLE_SELECT
    ) {
        result.multipleSelectOptionQuestions =
            getMultipleSelectOptionQuestions();
    }
    return result;
};

export const selectForm = (state: RootState) => state.qualityForm.data.form;
export const selectFormType = (state: RootState) =>
    state.qualityForm.data.formType;

/** UI refresh (QM-22699) applies only to evaluation and survey form editors. */
export function isSurveyOrEvaluationForm(
    formType: FormType | undefined
): boolean {
    return formType === FormType.SURVEY || formType === FormType.EVALUATION;
}

/** Form-type + FT gate for the QM-22699 eval/survey UI refresh. */
export function isSurveyOrEvaluationUiRefreshEnabled(
    formType: FormType | undefined,
    isFeatureEnabled: boolean
): boolean {
    return isSurveyOrEvaluationForm(formType) && isFeatureEnabled;
}

// Feature toggle to enable UI refresh for evaluation and survey form editors.
export const selectIsSurveyOrEvaluationForm = (state: RootState) =>
    isSurveyOrEvaluationUiRefreshEnabled(
        selectFormType(state),
        selectFeatureToggle(
            state,
            FeatureToggle.QM_22699_EVAL_SURVEY_UI_IMPROVEMENT
        )
    );

/**
 * Function that helps logic gets to a question object.
 * This function is used agnostically of question type, just return a question and that's it.
 */
export const selectQuestion = (state: RootState, questionId: string) => {
    const questions = state.qualityForm.data.questions;
    return questions?.[questionId];
};

/**
 * Function that helps logic gets to a scorable question.
 * When isMultipleSelect is true, the question id represents one of the MSQ answers which is literally a YES/NO MCQ question.
 * When isMultipleSelect is false, the question id represents a regular MCQ question.
 */
export const selectScorableQuestion = (
    state: RootState,
    questionId: string,
    isMultipleSelect?: boolean
) => {
    const data = state.qualityForm.data;
    let questions;
    if (isMultipleSelect) {
        questions = data.multipleSelectOptionQuestions;
    } else {
        questions = data.questions;
    }
    return questions?.[questionId];
};

export const selectAnswerOptionIdList = (
    state: RootState,
    questionId: string,
    isMultipleSelect?: boolean
): Readonly<Array<string> | undefined> => {
    const question = selectQuestion(state, questionId);
    if (isMultipleSelect) {
        return question?.multipleSelectOptionQuestions as Array<string>;
    } else {
        return question?.answerOptions as Array<string>;
    }
};

/**
 * This function gives the immutable-signified answer option for both MCQ and MSQ.
 * @param state
 * @param answerOptionId
 * @param isMultipleSelect
 * @returns
 */
export const selectAnswerOption = createSelector(
    [
        (
            state: RootState,
            answerOptionId: string,
            isMultipleSelect?: boolean
        ) =>
            isMultipleSelect
                ? state.qualityForm.data.multipleSelectOptionQuestions?.[
                      answerOptionId
                  ]
                : undefined,
        (
            state: RootState,
            answerOptionId: string,
            isMultipleSelect?: boolean
        ) =>
            isMultipleSelect
                ? undefined
                : selectMCQAnswerOption(state, answerOptionId),
        (_state: RootState, answerOptionId: string) => answerOptionId,
    ],
    (
        mSelectOptionsQ,
        mcqAnswerOption,
        answerOptionId
    ): Readonly<AnswerOption | undefined> => {
        if (mSelectOptionsQ) {
            // *** Note this only returns AnswerOption proxy object as multiple selection has not actual AnswerOption
            // like any other regular question
            let value;
            let assistanceConditions;
            const answerOptions = mSelectOptionsQ.answerOptions;
            (answerOptions || []).some((option) => {
                if (option.builtInType === "Selected") {
                    value = option.value;
                    assistanceConditions = option.assistanceConditions;
                    return true;
                }
                return false;
            });
            return {
                id: answerOptionId,
                contextId: mSelectOptionsQ?.contextId,
                text: mSelectOptionsQ?.text,
                value,
                assistanceConditions,
            };
        }
        return mcqAnswerOption;
    }
);

/**
 * This function gives the mutable answer option for both MCQ and MSQ.
 * @param state
 * @param answerOptionId
 * @param isMultipleSelect
 * @returns
 */
export const selectAnswerOptionMutable = (
    // ***NOTE THIS IS QualityFormState NOT RootState***
    state: QualityFormState,
    answerOptionId: string,
    isMultipleSelect?: boolean,
    isSelected = true
) => {
    if (!isMultipleSelect) {
        return selectMCQAnswerOption(
            { qualityForm: state } as RootState,
            answerOptionId
        );
    }
    const data = state.data;
    let answerOption: AnswerOption | undefined;
    const mSelectOptionsQ =
        data.multipleSelectOptionQuestions?.[answerOptionId];
    if (mSelectOptionsQ) {
        const answerOptions = mSelectOptionsQ.answerOptions;
        (answerOptions || []).some((option) => {
            if (
                isSelected &&
                option.builtInType === AnswerOptionBuiltInTypeEnum.Selected
            ) {
                answerOption = option;
                return true;
            } else if (
                !isSelected &&
                option.builtInType === AnswerOptionBuiltInTypeEnum.Unselected
            ) {
                answerOption = option;
                return true;
            }
            return false;
        });
    }
    return answerOption;
};

export const selectAnswerOptionsForQuestion = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    if (question?.type === BaseQuestionType.NpsQuestion) {
        return Object.keys(NPSScoreCategories);
    }
    if (question?.type === BaseQuestionType.MultipleChoiceQuestion) {
        return question.answerOptions;
    }
    if (question?.type === BaseQuestionType.MultipleSelectQuestion) {
        return question.multipleSelectOptionQuestions;
    }
};

export const selectAssistanceConditions = (
    state: RootState,
    answerId: string,
    isMultipleSelect?: boolean
) => {
    const answerOption = selectAnswerOption(state, answerId, isMultipleSelect);
    return answerOption?.assistanceConditions;
};

export const selectEvaluationFormType = (
    state: RootState
): EvaluationFormEvaluationFormTypeEnum | undefined => {
    const evaluationFormType = state.qualityForm.data.form?.evaluationFormType;
    return evaluationFormType ?? undefined;
};

export const selectIsAutomatedEvaluationForm = (state: RootState): boolean => {
    return (
        selectEvaluationFormType(state) ===
        EvaluationFormEvaluationFormTypeEnum.Automated
    );
};

/**
 * Return the object mapping of each answer option ID with corresponding Selected and Unselected option IDs for the particular question
 * @param state
 * @param questionId
 */

export const getMultipleSelectQuestionOptionsMapping = createSelector(
    [
        (state: RootState, questionId: string) =>
            selectQuestion(state, questionId),
        (state: RootState) => state.qualityForm,
    ],
    (question, qualityFormState) => {
        const idMappings: Record<
            string,
            { Selected: string | undefined; Unselected: string | undefined }
        > = {};
        if (
            question &&
            question.type === BaseQuestionType.MultipleSelectQuestion
        ) {
            question?.multipleSelectOptionQuestions?.forEach((answerOption) => {
                const selectedOptionId = selectAnswerOptionMutable(
                    qualityFormState,
                    answerOption,
                    true,
                    true
                )?.id;
                const unselectedOptionId = selectAnswerOptionMutable(
                    qualityFormState,
                    answerOption,
                    true,
                    false
                )?.id;
                idMappings[answerOption] = {
                    Selected: selectedOptionId,
                    Unselected: unselectedOptionId,
                };
            });
        }
        return idMappings;
    }
);

export const getQuestionTotalScore = (
    state: RootState,
    questionId: string,
    isMultipleSelect?: boolean
): number => {
    // go through all the multiple selection option item IDs and sum up their value
    let totalScore = 0;
    const answerOptionIdList = selectAnswerOptionIdList(
        state,
        questionId,
        isMultipleSelect
    );
    answerOptionIdList?.forEach((answerOptionId) => {
        const answerOption = selectAnswerOption(
            state,
            answerOptionId,
            isMultipleSelect
        );
        if (answerOption && answerOption.value) {
            totalScore += answerOption.value;
        }
    });
    return totalScore;
};

/**
 * Helper function to create option score map and validate MSQ question
 */
const createMSQOptionScoreMap = (
    question: any,
    questionScore: any
): Map<string, EvaluationQuestionScore> | undefined => {
    if (
        !question ||
        question.type !== BaseQuestionType.MultipleSelectQuestion
    ) {
        return undefined;
    }
    return new Map(
        questionScore?.multipleSelectQuestionOptionScores
            ?.filter((score) => !isUndefinedOrNull(score.questionId))
            .map((score) => [score.questionId, score])
    ) as Map<string, EvaluationQuestionScore>;
};

/**
 * Return the list of answer IDs that are currently selected in the Multiple Select Question
 * @param state
 * @param questionId
 * @returns
 */
export const selectCurrentAnswerIdsForMSQ = createSelector(
    [
        (state: RootState, questionId: string) =>
            selectQuestion(state, questionId),
        (state: RootState) => state.qualityForm,
        (state: RootState, questionId: string) =>
            state.scorable.data.questionScores[questionId],
    ],
    (question, qualityFormState, questionScore) => {
        const optionScoreMap = createMSQOptionScoreMap(question, questionScore);
        if (!optionScoreMap || !question) {
            return undefined;
        }

        return (question.multipleSelectOptionQuestions || []).filter(
            (optionId) => {
                const optionScore = optionScoreMap.get(optionId);
                if (!optionScore) return false;
                const expectedAnswerId = selectAnswerOptionMutable(
                    qualityFormState,
                    optionId,
                    true,
                    true
                )?.id;
                return (
                    expectedAnswerId &&
                    expectedAnswerId === optionScore.answerId
                );
            }
        );
    }
);

/**
 *
 * @param state
 * @param questionId
 * @returns the object where key is answer option main ID and the value is the assisted answer ID
 */
export const selectAssistedAnswerIdsForMSQ = createSelector(
    [
        (state: RootState, questionId: string) =>
            selectQuestion(state, questionId),
        (state: RootState, questionId: string) =>
            state.scorable.data.questionScores[questionId],
    ],
    (question, questionScore) => {
        const optionScoreMap = createMSQOptionScoreMap(question, questionScore);
        if (!optionScoreMap || !question) {
            return undefined;
        }

        return (question.multipleSelectOptionQuestions || []).reduce(
            (result: Record<string, string>, optionId) => {
                const optionScore = optionScoreMap.get(optionId);
                if (optionScore?.assistedAnswerId) {
                    result[optionId] = optionScore.assistedAnswerId;
                }
                return result;
            },
            {}
        );
    }
);

export const isVisibilityConditionEligibleQuestionType = (
    questionType: any,
    isMultipleSelectFTEnabled = false
): boolean => {
    let result =
        questionType === BaseQuestionType.MultipleChoiceQuestion ||
        questionType === BaseQuestionType.NpsQuestion;
    if (isMultipleSelectFTEnabled) {
        result =
            result || questionType === BaseQuestionType.MultipleSelectQuestion;
    }
    return result;
};

export const selectMaxPermissibleQuestionsWithAiScoring = createSelector(
    [
        (state: RootState) =>
            selectFeatureToggle(
                state,
                FeatureToggle.MAX_AI_SCORING_QUESTION_OUTER_LIMIT
            ),
        selectEvaluationFormAiScoringQuestionsMaxOuterLimit,
    ],
    (outerLimitToggleEnabled, outerLimitFromApi) => {
        const defaultMax = DefaultLimits.maxPermissibleQuestionsWithAiScoringV2;
        if (!outerLimitToggleEnabled) {
            return defaultMax;
        }
        return outerLimitFromApi ?? defaultMax;
    }
);

export const selectMaxAiScoringAnswerOptionCount = (state: RootState) => {
    const isWEM1532Enabled = selectFeatureToggle(
        state,
        FeatureToggle.AI_SCORING_LIMIT_INCREASE_FOR_ANSWER_OPTIONS
    );

    return isWEM1532Enabled
        ? DefaultLimits.maxAIScoringAnswerOptionCountV2
        : DefaultLimits.maxAIScoringAnswerOptionCount;
};

export const selectSelectedAutomatedScoringType = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    return question?.automatedScoringType;
};

export const selectAutomatedScoringFocus = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    return (
        question?.automatedScoringFocus ||
        EvaluationQuestionAutomatedScoringFocusEnum.FullInteraction
    );
};

/**
 * Determines if automated evaluation features are enabled.
 * Requires both AUTOMATED_EVALUATION_COMPLETION and EVALUATION_FORM_DEFAULT_ANSWERING
 * feature toggles to be enabled.
 */
export const selectAutomatedEvaluationFeatureEnabled = (state: RootState) =>
    selectFeatureToggle(state, FeatureToggle.AUTOMATED_EVALUATION_COMPLETION) &&
    selectFeatureToggle(state, FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING);

/**
 * Selects the ID of the selected option for a Multiple Select Answer (MSQ)
 * @param state - The root state of the application
 * @param answerOptionId - The ID of the answer option to check
 * @returns The ID of the selected option if found, undefined otherwise
 */
export const selectSelectedOptionIdForMSQ = (
    state: RootState,
    answerOptionId: string
) => {
    const data = state.qualityForm.data;

    const mSelectOptionsQ =
        data.multipleSelectOptionQuestions?.[answerOptionId];

    if (mSelectOptionsQ) {
        let selectedId;
        const answerOptions = mSelectOptionsQ.answerOptions;
        (answerOptions || []).some((option) => {
            if (option.builtInType === "Selected") {
                selectedId = option.id;
                return true;
            }
            return false;
        });
        return selectedId;
    }
};

/**
 * Checks if an answer option is the default answer for a question
 * @param state - The root state of the application
 * @param questionId - The ID of the question to check
 * @param optionId - The ID of the answer option to check
 * @param isMultipleSelect - Whether the question is a multiple select question
 * @returns Boolean indicating if the option is the default answer
 */
export const selectIsAnswerDefaultAnswer = (
    state: RootState,
    questionId: string,
    optionId: string,
    isMultipleSelect: boolean
) => {
    if (isMultipleSelect) {
        const multipleSelectOptionQuestion =
            state.qualityForm.data.multipleSelectOptionQuestions?.[optionId];

        const selectedAnswerOption = selectSelectedOptionIdForMSQ(
            state,
            optionId
        );

        return (
            !!multipleSelectOptionQuestion?.defaultAnswer?.id &&
            multipleSelectOptionQuestion?.defaultAnswer.id ===
                selectedAnswerOption
        );
    } else {
        const question = selectQuestion(state, questionId);
        return (
            !!question?.defaultAnswer?.id &&
            question?.defaultAnswer.id === optionId
        );
    }
};

/**
 * Checks if a default answer is selected for a given question
 * @param state - The root state of the application
 * @param questionId - The ID of the question to check
 * @returns Boolean indicating if a default answer is selected
 */
export const selectIsAnyDefaultAnswerSelectedForQuestion = (
    state: RootState,
    questionId: string
) => {
    const question = selectQuestion(state, questionId);
    return !!question?.defaultAnswer?.id;
};

/**
 * Checks if any question in the given list of question IDs is marked as critical
 * This includes checking both the main question and any multiple select answer option questions
 * @param state - The root state of the application
 * @param questionIds - Array of question IDs to check
 * @returns Boolean indicating if any of the questions are critical
 */
export const hasAnyCriticalQuestions = (
    state: RootState,
    questionIds: string[]
): boolean => {
    return questionIds.some((questionId) => {
        const question = selectQuestion(state, questionId);

        // Check if the main question is critical
        if (question?.isCritical) {
            return true;
        }

        // Check if any multiple select answer option questions are critical
        const multipleSelectOptionQuestionIds =
            question?.multipleSelectOptionQuestions;
        if (
            multipleSelectOptionQuestionIds &&
            multipleSelectOptionQuestionIds.length > 0
        ) {
            return multipleSelectOptionQuestionIds.some((optionQuestionId) => {
                const optionQuestion = selectScorableQuestion(
                    state,
                    optionQuestionId,
                    true
                );
                return !!optionQuestion?.isCritical;
            });
        }

        return false;
    });
};
